import {CATEGORY, WP_API_URL} from '../config';
import {Article, Category} from '../types';
import {htmlToText, parseJsonPost, upgradeToHttps} from '../utils/html';

interface WpMedia {
    source_url: string;
    media_details?: { sizes?: Record<string, { source_url: string }> };
}

interface WpPost {
    id: number;
    slug: string;
    date: string;
    date_gmt?: string;
    modified_gmt?: string;
    categories: number[];
    title: { rendered: string };
    excerpt: { rendered: string };
    content?: { rendered: string };
    _embedded?: { 'wp:featuredmedia'?: WpMedia[] };
}

async function getJson<T>(path: string): Promise<T> {
    const response = await fetch(`${WP_API_URL}${path}`);
    if (!response.ok) {
        throw new Error(`WordPress answered ${response.status} for ${path}`);
    }
    return response.json() as Promise<T>;
}

/** Embeds the featured image, so no extra request per post is needed. */
const EMBED_MEDIA = '_embed=wp:featuredmedia';
const ARTICLE_FIELDS = '_fields=id,slug,date,date_gmt,modified_gmt,categories,title,excerpt,_links,_embedded';

function featuredImage(post: WpPost): string | undefined {
    const media = post._embedded?.['wp:featuredmedia']?.[0];
    // "large" (max. 1024px) is plenty everywhere on the site and much lighter than the original upload
    return upgradeToHttps(media?.media_details?.sizes?.large?.source_url ?? media?.source_url);
}

/** WordPress ends excerpts with " [&hellip;]" */
const EXCERPT_ELLIPSIS = /\s*\[(?:…|\.\.\.)]\s*$/;

function toArticle(post: WpPost): Article {
    return {
        id: post.id,
        slug: post.slug,
        date: post.date,
        published: post.date_gmt && `${post.date_gmt}Z`,
        modified: post.modified_gmt && `${post.modified_gmt}Z`,
        categories: post.categories,
        title: htmlToText(post.title.rendered).trim(),
        excerpt: htmlToText(post.excerpt.rendered).trim().replace(EXCERPT_ELLIPSIS, ' …'),
        content: post.content && upgradeToHttps(post.content.rendered),
        imageUrl: featuredImage(post),
    };
}

/** The newest 100 news posts (without their content), newest first. */
export async function fetchArticles(): Promise<Article[]> {
    const posts = await getJson<WpPost[]>(
        `/posts?per_page=100&categories_exclude=${CATEGORY.hidden}&${EMBED_MEDIA}&${ARTICLE_FIELDS}`);
    return posts.filter(post => !post.categories.includes(CATEGORY.config)).map(toArticle);
}

export async function fetchPost(id: string | number): Promise<Article> {
    return toArticle(await getJson<WpPost>(`/posts/${encodeURIComponent(id)}?${EMBED_MEDIA}`));
}

export function fetchCategories(): Promise<Category[]> {
    return getJson<Category[]>(`/categories?per_page=100&parent=${CATEGORY.newsRoot}&hide_empty=true&_fields=id,name,slug`);
}

/** All JSON posts of the "Data" category, keyed by post title. Posts with invalid JSON are skipped. */
export async function fetchConfigPosts(): Promise<Record<string, unknown>> {
    const posts = await getJson<Pick<WpPost, 'title' | 'content'>[]>(
        `/posts?categories=${CATEGORY.config}&per_page=100&_fields=title,content`);

    const config: Record<string, unknown> = {};
    for (const post of posts) {
        const name = htmlToText(post.title.rendered).trim();
        try {
            config[name] = parseJsonPost(post.content?.rendered ?? '');
        } catch (error) {
            console.error(`Config post "${name}" does not contain valid JSON`, error);
        }
    }
    return config;
}
